import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { revertPostCI } from '../lib/useColumnInches'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../components/Toast'
import { canModerate } from '../lib/supabase'
import { awardCommentCI } from '../lib/useColumnInches'
import RankBadge from '../components/RankBadge'

export default function PostPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, profile, loading: authLoading } = useAuth()
  const toast = useToast()
  const [post, setPost] = useState(null)
  const [responses, setResponses] = useState([])
  const [comments, setComments] = useState([])
  const [commentText, setCommentText] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [editingCommentId, setEditingCommentId] = useState(null)
  const [editingCommentText, setEditingCommentText] = useState('')

  // Annotation state
  const [annotations, setAnnotations] = useState([])
  const [pendingAnnotation, setPendingAnnotation] = useState(null) // { anchor, x, y }
  const [annotationText, setAnnotationText] = useState('')
  const [savingAnnotation, setSavingAnnotation] = useState(false)
  const [showAnnotations, setShowAnnotations] = useState(false)
  const bodyRef = useRef(null)

  useEffect(() => {
    fetchPost()
    fetchComments()
    fetchAnnotations()
  }, [id, authLoading])

  async function fetchPost() {
    if (authLoading) return
    const { data, error } = await supabase
      .from('posts')
      .select('*, profiles!posts_author_id_fkey(id, display_name, role, column_inches, is_voice)')
      .eq('id', id)
      .single()
    if (error || !data) { navigate('/'); return }
    if (data.status !== 'published' && !canModerate(profile?.role) && data.author_id !== user?.id) {
      navigate('/'); return
    }
    setPost(data)
    setLoading(false)
    // Fetch argument thread responses
    const { data: resp } = await supabase
      .from('posts')
      .select('id, title, excerpt, created_at, author_id, profiles!posts_author_id_fkey(display_name)')
      .eq('response_to', id)
      .eq('status', 'published')
      .order('created_at', { ascending: true })
    setResponses(resp || [])
  }

  async function fetchComments() {
    const { data } = await supabase
      .from('comments')
      .select('*, profiles!comments_author_id_fkey(id, display_name, role, column_inches, is_voice)')
      .eq('post_id', id)
      .order('created_at', { ascending: true })
    setComments(data || [])
  }

  async function fetchAnnotations() {
    const { data } = await supabase
      .from('annotations')
      .select('*, profiles!annotations_author_id_fkey(display_name)')
      .eq('post_id', id)
      .order('created_at', { ascending: true })
    setAnnotations(data || [])
  }

  // Handle text selection for annotation
  function handleBodyMouseUp(e) {
    if (!user) return
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed || !selection.toString().trim()) return
    const anchor = selection.toString().trim().slice(0, 200)
    const range = selection.getRangeAt(0)
    const rect = range.getBoundingClientRect()
    const bodyRect = bodyRef.current?.getBoundingClientRect()
    if (!bodyRect) return
    setPendingAnnotation({
      anchor,
      x: rect.left - bodyRect.left + rect.width / 2,
      y: rect.top - bodyRect.top - 8,
    })
    setAnnotationText('')
  }

  async function saveAnnotation() {
    if (!annotationText.trim() || !pendingAnnotation) return
    setSavingAnnotation(true)
    const { error } = await supabase.from('annotations').insert({
      post_id: parseInt(id),
      author_id: user.id,
      anchor_text: pendingAnnotation.anchor,
      note: annotationText.trim(),
    })
    if (error) { toast(error.message, 'error') }
    else { toast('Annotation saved.', 'success'); fetchAnnotations(); setPendingAnnotation(null) }
    setSavingAnnotation(false)
  }

  async function saveCommentEdit(commentId) {
    if (!editingCommentText.trim()) return
    const { error } = await supabase.from('comments').update({ content: editingCommentText.trim() }).eq('id', commentId)
    if (error) { toast(error.message, 'error'); return }
    setEditingCommentId(null)
    fetchComments()
    toast('Comment updated.', 'success')
  }

  async function submitComment(e) {
    e.preventDefault()
    if (!user) { navigate('/auth'); return }
    if (!commentText.trim()) return
    setSubmitting(true)
    const { error } = await supabase.from('comments')
      .insert({ post_id: id, author_id: user.id, content: commentText.trim() })
    if (error) { toast(error.message, 'error') }
    else {
      setCommentText('')
      fetchComments()
      toast('Comment posted.', 'success')
      if (post?.author_id && post.author_id !== user.id) {
        awardCommentCI(post.author_id, id).catch(console.error)
        supabase.from('notifications').insert({
          user_id: post.author_id, actor_id: user.id, post_id: parseInt(id),
          type: 'comment', message: `${profile?.display_name || 'Someone'} commented on "${post.title}"`,
        }).catch(console.error)
      }
    }
    setSubmitting(false)
  }

  async function deleteComment(commentId) {
    if (!window.confirm('Delete this comment?')) return
    const { error } = await supabase.from('comments').delete().eq('id', commentId)
    if (error) toast(error.message, 'error')
    else { fetchComments(); toast('Comment deleted.', 'success') }
  }

  async function deletePost() {
    if (!canModerate(profile?.role)) return
    if (!window.confirm('Delete this post? This cannot be undone.')) return
    if (post?.status === 'published' && post?.author_id) {
      revertPostCI(post.author_id, post).catch(console.error)
    }
    await supabase.from('posts').delete().eq('id', id)
    toast('Post deleted.', 'success')
    navigate('/')
  }

  // Claim anonymous post
  async function claimPost() {
    if (!user) { navigate('/auth'); return }
    if (!window.confirm('Claim this piece? Your name will appear as the author.')) return
    const { error } = await supabase.from('posts')
      .update({ author_id: user.id, is_anonymous: false, claimed_by: user.id })
      .eq('id', id).eq('is_anonymous', true)
    if (error) toast(error.message, 'error')
    else { toast('Piece claimed.', 'success'); fetchPost() }
  }

  if (loading) return <div className="loading-screen"><div className="loading-logo">The Margins<span className="loading-logo-dot">.</span></div></div>
  if (!post) return null

  const date = new Date(post.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  const authorName = post.is_anonymous ? 'Anonymous' : (post.profiles?.display_name || 'The Margins')
  const authorLink = post.is_anonymous ? null : post.author_id

  // Find annotations that match visible text
  const highlightedAnnotations = annotations.filter(a =>
    showAnnotations && post.body?.includes(a.anchor_text)
  )

  return (
    <div style={styles.page}>
      {/* OG meta tags via document.title */}
      {post && (document.title = `${post.title} — The Margins`)}

      <div style={styles.readerNav}>
        <Link to={'/feed'} style={styles.backBtn}>← Back to the feed</Link>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {post.status !== 'published' && (
            <span className={`status-badge status-${post.status}`}>{post.status}</span>
          )}
          {annotations.length > 0 && (
            <button
              className={`btn btn-sm ${showAnnotations ? 'btn-accent' : 'btn-ghost'}`}
              onClick={() => setShowAnnotations(s => !s)}
              title="Toggle annotations"
            >
              ◐ {annotations.length} note{annotations.length !== 1 ? 's' : ''}
            </button>
          )}
          {post.is_anonymous && !post.author_id && user && (
            <button className="btn btn-sm btn-ghost" onClick={claimPost}>Claim this piece</button>
          )}
          {post.author_id === user?.id && (
            <button className="btn btn-sm btn-ghost" onClick={() => navigate(`/write?edit=${post.id}`)}>Edit</button>
          )}
          {canModerate(profile?.role) && (
            <button className="btn btn-sm btn-danger" onClick={deletePost}>Delete</button>
          )}
        </div>
      </div>

      <div style={styles.content}>
        <div style={styles.tag}>{post.tag || post.section}</div>
        <h1 style={styles.title}>{post.title}</h1>
        <div style={styles.meta}>
          <span>
            {authorLink
              ? <Link to={`/writer/${authorLink}`} style={{ color: 'var(--muted)' }}>{authorName}</Link>
              : <span>{authorName}</span>
            }
          </span>
          <span style={{ display: 'flex', gap: '1rem' }}>
            {post.read_time && <span>{post.read_time} min read</span>}
            <span>{date}</span>
          </span>
        </div>

        {post.excerpt && <p style={styles.excerpt}>{post.excerpt}</p>}
        <div style={styles.divider} />

        {/* Body with annotation selection */}
        <div
          ref={bodyRef}
          style={{ ...styles.body, position: 'relative', userSelect: 'text' }}
          className="post-body"
          dangerouslySetInnerHTML={{ __html: post.body }}
          onMouseUp={handleBodyMouseUp}
        />

        {/* Pending annotation popover */}
        {pendingAnnotation && (
          <div style={{
            position: 'absolute',
            left: `calc(${pendingAnnotation.x}px - 140px)`,
            top: `${pendingAnnotation.y + 200}px`,
            width: 280, background: 'var(--paper)', border: '1px solid var(--rule)',
            borderRadius: 10, padding: '0.85rem 1rem', boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            zIndex: 200,
          }}>
            <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.54rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.5rem' }}>
              Leave a note on:
            </p>
            <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.78rem', color: 'var(--muted)', fontStyle: 'italic', marginBottom: '0.75rem', lineHeight: 1.5 }}>
              "{pendingAnnotation.anchor.slice(0, 80)}{pendingAnnotation.anchor.length > 80 ? '…' : ''}"
            </p>
            <textarea
              className="form-textarea"
              value={annotationText}
              onChange={e => setAnnotationText(e.target.value)}
              placeholder="Your note..."
              style={{ minHeight: 60, fontSize: '0.82rem', marginBottom: '0.5rem' }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-sm btn-accent" onClick={saveAnnotation} disabled={savingAnnotation || !annotationText.trim()}>
                {savingAnnotation ? 'Saving...' : 'Save Note'}
              </button>
              <button className="btn btn-sm btn-ghost" onClick={() => setPendingAnnotation(null)}>Cancel</button>
            </div>
          </div>
        )}

        {/* Annotation hint */}
        {user && !pendingAnnotation && (
          <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.52rem', color: 'var(--muted)', marginTop: '1rem', letterSpacing: '0.06em', opacity: 0.7 }}>
            ◐ Select any text to leave a marginal note
          </p>
        )}

        {/* Annotations list */}
        {showAnnotations && annotations.length > 0 && (
          <div style={{ marginTop: '2rem', padding: '1.25rem', background: 'var(--cream)', borderRadius: 10, border: '1px solid var(--rule)' }}>
            <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '1rem' }}>
              ◐ Marginal Notes ({annotations.length})
            </p>
            {annotations.map(a => (
              <div key={a.id} style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--rule)' }}>
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.78rem', color: 'var(--muted)', fontStyle: 'italic', marginBottom: '0.4rem' }}>
                  "{a.anchor_text.slice(0, 100)}{a.anchor_text.length > 100 ? '…' : ''}"
                </p>
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.88rem', color: 'var(--ink)', lineHeight: 1.6, marginBottom: '0.3rem' }}>{a.note}</p>
                <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.54rem', color: 'var(--muted)' }}>
                  — {a.profiles?.display_name} · {new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Argument Threads — responses */}
        {responses.length > 0 && (
          <div style={styles.responsesSection}>
            <h3 style={styles.responsesTitle}>◈ The Thread — {responses.length} Response{responses.length !== 1 ? 's' : ''}</h3>
            <p style={styles.responsesDesc}>Writers who responded to this piece with their own take.</p>
            {responses.map(r => (
              <Link key={r.id} to={`/post/${r.id}`} style={styles.responseCard}>
                <div style={styles.responseTitle}>{r.title}</div>
                {r.excerpt && <p style={styles.responseExcerpt}>{r.excerpt}</p>}
                <div style={styles.responseMeta}>
                  by {r.profiles?.display_name} · {new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Write a Response */}
        {user && post.status === 'published' && (
          <div style={styles.writeResponseBanner}>
            <div>
              <p style={styles.writeResponseTitle}>Have a take?</p>
              <p style={styles.writeResponseDesc}>Write a full response piece. It'll be linked here as part of the argument thread.</p>
            </div>
            <button
              className="btn btn-accent"
              onClick={() => navigate(`/write?response=${id}`)}
            >
              ✎ Write a Response
            </button>
          </div>
        )}

        {/* COMMENTS */}
        <div style={styles.commentsSection}>
          <div style={styles.commentsHeader}>
            <h2 style={styles.commentsTitle}>Discussion</h2>
            <span style={styles.commentCount}>{comments.length} {comments.length === 1 ? 'comment' : 'comments'}</span>
          </div>

          {user ? (
            <form onSubmit={submitComment} style={styles.commentForm}>
              <textarea
                className="form-textarea"
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Share your thoughts on this piece..."
                style={{ minHeight: 100 }}
              />
              <button type="submit" className="btn btn-ink btn-sm" disabled={submitting || !commentText.trim()}>
                {submitting ? 'Posting...' : 'Post Comment'}
              </button>
            </form>
          ) : (
            <div style={styles.signInPrompt}>
              <p style={styles.signInText}>
                <Link to="/auth" style={{ color: 'var(--ink)', textDecoration: 'underline' }}>Sign in</Link> to join the discussion.
              </p>
            </div>
          )}

          <div style={styles.commentsList}>
            {comments.length === 0 ? (
              <div className="empty-state" style={{ border: 'none', padding: '2rem 0' }}>
                <p>No comments yet. Start the discussion.</p>
              </div>
            ) : comments.map(comment => (
              <div key={comment.id} style={styles.comment}>
                <div style={styles.commentHeader}>
                  <div style={styles.commentAuthor}>
                    <span style={styles.commentAvatar}>{comment.profiles?.display_name?.[0]?.toUpperCase()}</span>
                    <span style={styles.commentName}>
                      <Link to={`/writer/${comment.author_id}`} style={{ color: 'inherit' }}>{comment.profiles?.display_name}</Link>
                    </span>
                    <RankBadge columnInches={comment.profiles?.column_inches || 0} isVoice={comment.profiles?.is_voice || false} size="sm" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={styles.commentDate}>
                      {new Date(comment.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    {comment.author_id === user?.id && editingCommentId !== comment.id && (
                      <button className="btn btn-sm btn-ghost" onClick={() => { setEditingCommentId(comment.id); setEditingCommentText(comment.content) }}>Edit</button>
                    )}
                    {(canModerate(profile?.role) || comment.author_id === user?.id) && (
                      <button className="btn btn-sm btn-danger" onClick={() => deleteComment(comment.id)}>Delete</button>
                    )}
                  </div>
                </div>
                {editingCommentId === comment.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <textarea
                      className="form-textarea"
                      value={editingCommentText}
                      onChange={e => setEditingCommentText(e.target.value)}
                      style={{ minHeight: 80, fontSize: '0.95rem' }}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn btn-sm btn-accent" onClick={() => saveCommentEdit(comment.id)}>Save</button>
                      <button className="btn btn-sm btn-ghost" onClick={() => setEditingCommentId(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <p style={styles.commentContent}>{comment.content}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

const styles = {
  page: { paddingTop: '4.5rem', minHeight: '100vh' },
  readerNav: { position: 'sticky', top: '4.5rem', background: 'var(--paper)', borderBottom: '1px solid var(--rule)', padding: '0.9rem 3rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 100 },
  backBtn: { fontFamily: "'DM Mono', monospace", fontSize: '0.68rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' },
  content: { maxWidth: 740, margin: '0 auto', padding: '4rem 2rem 6rem', position: 'relative' },
  tag: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '1.2rem' },
  title: { fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(2rem, 5vw, 3.2rem)', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: '1.5rem' },
  meta: { display: 'flex', justifyContent: 'space-between', fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.06em', color: 'var(--muted)', paddingBottom: '2rem', borderBottom: '1px solid var(--rule)' },
  excerpt: { fontFamily: "'Space Grotesk', sans-serif", fontStyle: 'italic', fontSize: '1.15rem', lineHeight: 1.7, color: 'var(--muted)', margin: '2rem 0', paddingBottom: '2rem', borderBottom: '1px solid var(--rule)' },
  divider: { height: 1, background: 'var(--rule)', margin: '0 0 2.5rem' },
  body: { fontSize: '1.1rem', lineHeight: 1.85, color: 'var(--ink)' },
  responsesSection: { marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid var(--rule)' },
  responsesTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.4rem' },
  responsesDesc: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '0.06em', marginBottom: '1.25rem' },
  responseCard: { display: 'block', padding: '1rem 1.25rem', background: 'var(--cream)', border: '1px solid var(--rule)', borderRadius: 8, marginBottom: '0.75rem', textDecoration: 'none', color: 'inherit', transition: 'border-color 0.15s' },
  responseTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700, marginBottom: '0.3rem', color: 'var(--ink)' },
  responseExcerpt: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.85rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: '0.5rem' },
  responseMeta: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', color: 'var(--muted)', letterSpacing: '0.06em' },
  writeResponseBanner: { display: 'flex', gap: '1.5rem', alignItems: 'center', justifyContent: 'space-between', marginTop: '2.5rem', padding: '1.25rem 1.5rem', background: 'rgba(200,64,26,0.05)', border: '1px solid rgba(200,64,26,0.2)', borderRadius: 10, flexWrap: 'wrap' },
  writeResponseTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700, marginBottom: '0.3rem' },
  writeResponseDesc: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.82rem', color: 'var(--muted)', lineHeight: 1.5 },
  commentsSection: { marginTop: '4rem', paddingTop: '3rem', borderTop: '2px solid var(--ink)' },
  commentsHeader: { display: 'flex', alignItems: 'baseline', gap: '1rem', marginBottom: '2rem' },
  commentsTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.6rem', fontWeight: 700 },
  commentCount: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.1em', color: 'var(--muted)' },
  commentForm: { marginBottom: '2.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  signInPrompt: { padding: '1.5rem', background: 'var(--cream)', border: '1px solid var(--rule)', borderRadius: 8, marginBottom: '2rem' },
  signInText: { fontFamily: "'DM Mono', monospace", fontSize: '0.72rem', color: 'var(--muted)', textAlign: 'center' },
  commentsList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  comment: { background: 'var(--paper)', padding: '1.5rem', border: '1px solid var(--rule)', borderRadius: 8 },
  commentHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' },
  commentAuthor: { display: 'flex', alignItems: 'center', gap: '0.6rem' },
  commentAvatar: { width: 28, height: 28, background: 'var(--ink)', color: 'var(--paper)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, borderRadius: '50%', flexShrink: 0 },
  commentName: { fontFamily: "'DM Mono', monospace", fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.05em' },
  commentDate: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: 'var(--muted)' },
  commentContent: { fontSize: '0.95rem', lineHeight: 1.7, color: 'var(--ink)' },
}
